import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client/react";
import { Plus, ClipboardList, ChevronRight, Loader2, PenLine, Sparkles, Trash2, Clock } from "lucide-react";
import { useState } from "react";

import { GET_SESSIONS } from "@/graphql/queries/sessions";
import { GET_STUDENTS } from "@/graphql/queries/students";
import { GET_SUBJECTS } from "@/graphql/queries/subjects";
import { CREATE_SESSION, GENERATE_EXERCISES, DELETE_SESSION } from "@/graphql/mutations/sessions";
import type { SessionListItem, Student, Subject } from "@/graphql/types";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { SubjectIcon } from "@/components/subject-icon";
import { SessionsFilters } from "@/components/sessions-filters";

// Format active working time (seconds) into a short Lithuanian label,
// e.g. "45 s", "12 min", "1 val. 5 min".
function formatDuration(totalSeconds: number): string {
  if (totalSeconds < 60) return `${totalSeconds} s`;
  const totalMinutes = Math.round(totalSeconds / 60);
  if (totalMinutes < 60) return `${totalMinutes} min`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes > 0 ? `${hours} val. ${minutes} min` : `${hours} val.`;
}

function getStatusBadge(status: string) {
  const variants: Record<string, { label: string; variant: "default" | "secondary" | "outline" | "destructive" }> = {
    DRAFT: { label: "Juodraštis", variant: "secondary" },
    GENERATED: { label: "Sugeneruota", variant: "outline" },
    GRADED: { label: "Įvertinta", variant: "default" },
    COMPLETED: { label: "Užbaigta", variant: "default" },
  };
  const { label, variant } = variants[status] || { label: status, variant: "secondary" as const };
  return <Badge variant={variant}>{label}</Badge>;
}

export default function SessionsListPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const studentFilter = searchParams.get("student") || undefined;
  const subjectFilter = searchParams.get("subject") || undefined;

  const [creatingSubject, setCreatingSubject] = useState<string | null>(null);
  const [deletingSessionId, setDeletingSessionId] = useState<string | null>(null);

  const { data: sessionsData, loading: sessionsLoading } = useQuery(GET_SESSIONS, {
    variables: { studentId: studentFilter, subjectKey: subjectFilter },
  });
  // When student is selected but no subject, also fetch all sessions for that student to derive recent subjects
  const { data: allStudentSessionsData } = useQuery(GET_SESSIONS, {
    variables: { studentId: studentFilter },
    skip: !studentFilter || !!subjectFilter,
  });
  const { data: studentsData } = useQuery(GET_STUDENTS);
  const { data: subjectsData } = useQuery(GET_SUBJECTS);

  const [createSession] = useMutation(CREATE_SESSION);
  const [generateExercises] = useMutation(GENERATE_EXERCISES);
  const [deleteSession] = useMutation(DELETE_SESSION);

  // How long a session this learner gets. Undefined lets the server decide,
  // which is what happens if the students query has not landed yet.
  const suggestedCountFor = (studentId: string): number | undefined =>
    studentsData?.students?.find((s: { id: string }) => s.id === studentId)?.suggestedExerciseCount;

  const handleQuickCreate = async (subjectKey: string, studentId?: string) => {
    const resolvedStudentId = studentId || studentFilter;
    if (!resolvedStudentId || creatingSubject) return;
    setCreatingSubject(subjectKey + (studentId || ""));
    try {
      const { data: createData } = await createSession({
        variables: { studentId: resolvedStudentId, subjectKey },
      });
      const sessionId = createData?.createSession?.session?.id;
      if (!sessionId) return;
      // Quick-create asks no questions, so it uses the length set for this
      // learner. Anyone who wants a different one changes it on the session.
      await generateExercises({
        variables: { sessionId, targetExerciseCount: suggestedCountFor(resolvedStudentId) },
      });

      // Navigate to session page - if exercises are missing, the detail
      // page will show specific links to generate them
      navigate(`/sessions/${sessionId}`);
    } catch (e) {
      console.error("Failed to create session:", e);
      setCreatingSubject(null);
    }
  };

  const handleDelete = async (sessionId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!confirm("Ar tikrai norite ištrinti šią sesiją?")) return;
    setDeletingSessionId(sessionId);
    try {
      await deleteSession({
        variables: { id: sessionId },
        refetchQueries: [{ query: GET_SESSIONS, variables: { studentId: studentFilter, subjectKey: subjectFilter } }],
      });
    } catch (err) {
      console.error("Failed to delete session:", err);
    } finally {
      setDeletingSessionId(null);
    }
  };

  if (sessionsLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const sessions: SessionListItem[] = sessionsData?.sessions ?? [];
  const students: Pick<Student, "id" | "name">[] = (studentsData?.students ?? []).map(
    (s: Student) => ({ id: s.id, name: s.name })
  );
  const subjects: Pick<Subject, "id" | "name" | "icon">[] = (subjectsData?.subjects ?? []).map(
    (s: Subject) => ({ id: s.id, name: s.name, icon: s.icon })
  );

  // Derive suggested subjects when student is selected but no subject filter
  const suggestedSubjects: { id: string; name: string; icon: string }[] = [];
  if (studentFilter && !subjectFilter) {
    const allSessions: SessionListItem[] = allStudentSessionsData?.sessions ?? [];
    const seen = new Set<string>();
    // Sessions are sorted by most recent first
    for (const s of allSessions) {
      if (s.subjectKey && !seen.has(s.subjectKey) && s.subject) {
        seen.add(s.subjectKey);
        suggestedSubjects.push({ id: s.subjectKey, name: s.subject.name, icon: s.subject.icon });
        if (seen.size >= 3) break;
      }
    }
  }

  // Derive recent student+subject combos when no filters are set
  const suggestedCombos: { studentId: string; studentName: string; subjectKey: string; subjectName: string; subjectIcon: string }[] = [];
  if (!studentFilter && !subjectFilter) {
    const seen = new Set<string>();
    for (const s of sessions) {
      const key = `${s.student.id}:${s.subjectKey}`;
      if (s.subjectKey && s.subject && !seen.has(key)) {
        seen.add(key);
        suggestedCombos.push({
          studentId: s.student.id,
          studentName: s.student.name,
          subjectKey: s.subjectKey,
          subjectName: s.subject.name,
          subjectIcon: s.subject.icon,
        });
        if (seen.size >= 3) break;
      }
    }
  }

  // Build new session URL with current filters
  const newSessionParams = new URLSearchParams();
  if (studentFilter) newSessionParams.set("studentId", studentFilter);
  if (subjectFilter) newSessionParams.set("subjectId", subjectFilter);
  const newSessionUrl = `/sessions/new${newSessionParams.toString() ? `?${newSessionParams.toString()}` : ""}`;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Sesijos</h1>
          <p className="text-muted-foreground">
            Peržiūrėkite ir valdykite mokymosi sesijas
          </p>
        </div>
        <Button asChild>
          <Link to={newSessionUrl}>
            <Plus className="mr-2 h-4 w-4" />
            Nauja sesija
          </Link>
        </Button>
      </div>

      <SessionsFilters students={students} subjects={subjects} />

      {/* Quick-create suggestion cards */}
      {(() => {
        // Case 1: student + subject selected → single suggestion
        if (studentFilter && subjectFilter) {
          const subject = subjects.find((s) => s.id === subjectFilter);
          if (subject) {
            return (
              <div className="space-y-2">
                <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Greitas startas</h2>
                <button
                  onClick={() => handleQuickCreate(subjectFilter)}
                  disabled={!!creatingSubject}
                  className="w-full text-left"
                >
                  <Card className="border-dashed border-2 border-primary/30 hover:border-primary/60 hover:bg-primary/5 transition-colors cursor-pointer">
                    <CardContent className="flex items-center gap-3 py-4">
                      {creatingSubject === subjectFilter ? (
                        <Loader2 className="h-5 w-5 animate-spin text-primary" />
                      ) : (
                        <Sparkles className="h-5 w-5 text-primary" />
                      )}
                      <div className="flex items-center gap-2">
                        <SubjectIcon icon={subject.icon} className="h-4 w-4 text-primary" />
                        <span className="font-medium">
                          Nauja sesija
                        </span>
                        <span className="text-muted-foreground">
                          — {subject.name}, 10 užduočių
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </button>
              </div>
            );
          }
        }
        // Case 2: student selected, no subject → suggest recent subjects
        if (studentFilter && !subjectFilter && suggestedSubjects.length > 0) {
          const studentName = students.find((s) => s.id === studentFilter)?.name;
          return (
            <div className="space-y-2">
              <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Greitas startas{studentName ? ` — ${studentName}` : ""}</h2>
              <div className="grid gap-2 sm:grid-cols-3">
                {suggestedSubjects.map((subject) => (
                  <button
                    key={subject.id}
                    onClick={() => handleQuickCreate(subject.id)}
                    disabled={!!creatingSubject}
                    className="text-left"
                  >
                    <Card className="border-dashed border-2 border-primary/30 hover:border-primary/60 hover:bg-primary/5 transition-colors cursor-pointer h-full">
                      <CardContent className="flex items-center gap-3 py-4">
                        {creatingSubject === subject.id ? (
                          <Loader2 className="h-5 w-5 animate-spin text-primary" />
                        ) : (
                          <Sparkles className="h-5 w-5 text-primary" />
                        )}
                        <div>
                          <div className="flex items-center gap-1.5 font-medium">
                            <SubjectIcon icon={subject.icon} className="h-4 w-4 text-primary" />
                            {subject.name}
                          </div>
                          <div className="text-sm text-muted-foreground">10 užduočių</div>
                        </div>
                      </CardContent>
                    </Card>
                  </button>
                ))}
              </div>
            </div>
          );
        }
        // Case 3: no filters → suggest recent student+subject combos
        if (!studentFilter && !subjectFilter && suggestedCombos.length > 0) {
          return (
            <div className="space-y-2">
              <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Greitas startas</h2>
              <div className="grid gap-2 sm:grid-cols-3">
                {suggestedCombos.map((combo) => {
                  const comboKey = combo.studentId + combo.subjectKey;
                  return (
                    <button
                      key={comboKey}
                      onClick={() => handleQuickCreate(combo.subjectKey, combo.studentId)}
                      disabled={!!creatingSubject}
                      className="text-left"
                    >
                      <Card className="border-dashed border-2 border-primary/30 hover:border-primary/60 hover:bg-primary/5 transition-colors cursor-pointer h-full">
                        <CardContent className="flex items-center gap-3 py-4">
                          {creatingSubject === comboKey ? (
                            <Loader2 className="h-5 w-5 animate-spin text-primary" />
                          ) : (
                            <Sparkles className="h-5 w-5 text-primary" />
                          )}
                          <div>
                            <div className="font-medium">{combo.studentName}</div>
                            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                              <SubjectIcon icon={combo.subjectIcon} className="h-3.5 w-3.5" />
                              {combo.subjectName} · 10 užduočių
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        }
        return null;
      })()}

      {sessions.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <ClipboardList className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-lg font-medium">Nėra sesijų</p>
            <p className="text-sm text-muted-foreground mb-4">
              Sukurkite pirmąją sesiją, kad pradėtumėte
            </p>
            <Button asChild>
              <Link to={newSessionUrl}>
                <Plus className="mr-2 h-4 w-4" />
                Sukurti sesiją
              </Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        (() => {
          const pendingSessions = sessions.filter((s) => s.status !== "COMPLETED");
          const completedSessions = sessions.filter((s) => s.status === "COMPLETED");

          const renderSessionCard = (session: SessionListItem, muted = false) => {
            // Answered questions only: a skipped one was never worked through, so
            // counting it here would score it exactly like a wrong answer.
            const answeredCount = session.exercises.filter(
              (ex) => ex.result !== "SKIPPED" && ex.result !== "PENDING"
            ).length;
            const correctCount = session.exercises.filter((ex) => ex.result === "CORRECT").length;

            const subjectName = session.subject?.name ?? null;
            const subjectIcon = session.subject?.icon ?? null;

            const isFinished = session.status === "COMPLETED";
            const scorePercent = answeredCount > 0 ? Math.round((correctCount / answeredCount) * 100) : 0;

            return (
              <Card key={session.id} className={muted ? "opacity-60" : undefined}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {session.student.avatarUrl ? (
                        <img
                          src={session.student.avatarUrl}
                          alt={session.student.name}
                          width={40}
                          height={40}
                          className="rounded-full"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center text-muted-foreground font-medium">
                          {session.student.name.charAt(0)}
                        </div>
                      )}
                      <div>
                        <CardTitle className="text-lg">{session.student.name}</CardTitle>
                        <CardDescription className="flex items-center gap-1">
                          {subjectName && (
                            <span className="font-medium inline-flex items-center gap-1">
                              {subjectIcon && <SubjectIcon icon={subjectIcon} className="h-3.5 w-3.5" />}
                              {subjectName} ·{" "}
                            </span>
                          )}
                          {new Date(session.createdAt).toLocaleDateString("lt-LT", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                          {session.activeSeconds > 0 && (
                            <span className="inline-flex items-center gap-1">
                              {" · "}
                              <Clock className="h-3.5 w-3.5" />
                              {formatDuration(session.activeSeconds)}
                            </span>
                          )}
                        </CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {session.status === "GRADED" && (
                        <Badge variant="outline" className="flex items-center gap-1 border-violet-300 text-violet-700 dark:border-violet-700 dark:text-violet-400 text-xs">
                          <PenLine className="h-3 w-3" />
                          Laukia peržiūros
                        </Badge>
                      )}
                      {isFinished && (
                        <Badge variant={scorePercent >= 70 ? "default" : "secondary"}>
                          {scorePercent}%
                        </Badge>
                      )}
                      {getStatusBadge(session.status)}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-muted-foreground">
                        {session.exercises.length > 0 ? (
                          <>
                            {session.exercises.length} užduotys
                            {isFinished && (
                              <span className="ml-2">
                                ({correctCount}/{answeredCount} teisingai)
                              </span>
                            )}
                          </>
                        ) : (
                          "Dar nesugeneruota užduočių"
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {!isFinished && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-muted-foreground hover:text-destructive"
                            onClick={(e) => handleDelete(session.id, e)}
                            disabled={deletingSessionId === session.id}
                          >
                            {deletingSessionId === session.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </Button>
                        )}
                        <Button variant="outline" size="sm" asChild>
                          <Link to={`/sessions/${session.id}`}>
                            Peržiūrėti
                            <ChevronRight className="ml-1 h-4 w-4" />
                          </Link>
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          };

          return (
            <div className="space-y-6">
              {pendingSessions.length > 0 && (
                <div className="space-y-4">
                  <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Nebaigtos ({pendingSessions.length})</h2>
                  {pendingSessions.map((s) => renderSessionCard(s))}
                </div>
              )}
              {completedSessions.length > 0 && (
                <div className="space-y-4">
                  <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Užbaigtos ({completedSessions.length})</h2>
                  {completedSessions.map((s) => renderSessionCard(s, true))}
                </div>
              )}
            </div>
          );
        })()
      )}
    </div>
  );
}
